import { useState } from 'react';
import { api } from '../api';
import { useApi } from '../lib/useApi';
import { Card, CardHead, ErrorState, Loading, PlanBadge } from '../components/ui';
import { gb, n0, plural, rub } from '../lib/format';
import { loadPlans } from '../lib/plans';
import { useAuth } from '../components/AuthProvider';

/* Расписание фоновых задач известно из кода бэкенда; отдельного эндпоинта,
   который бы его отдавал, нет — поэтому список зашит здесь. */
/* Цена и лимиты тарифа лежат внутри версии: цены отдельным списком,
   лимиты — атрибутами. Достаём то, что нужно для строки списка. */
const attr = (plan, code) => (plan.attributes || []).find((a) => a.code === code);

function monthlyPrice(plan) {
  const prices = plan.prices || [];
  const monthly = prices.find((x) => x.periodUnit === 'MONTH' && (x.periodValue || 1) === 1) || prices[0];
  if (!monthly) return null;
  const value = Number(monthly.price) || 0;
  if (monthly.periodUnit === 'YEAR') return Math.round(value / 12 / (monthly.periodValue || 1));
  return Math.round(value / (monthly.periodValue || 1));
}

function limitsLine(plan) {
  const seats = attr(plan, 'available_user_count');
  const space = attr(plan, 'available_space_mb');
  const projects = attr(plan, 'available_project_count');
  const parts = [];
  if (seats) parts.push(`${seats.value} ${plural(Number(seats.value) || 0, 'место', 'места', 'мест')}`);
  if (space) parts.push(gb(Number(space.value) || 0));
  if (projects) parts.push(projects.unlimited ? '∞ проектов' : `${projects.value} ${plural(Number(projects.value) || 0, 'проект', 'проекта', 'проектов')}`);
  return parts.join(' · ') || 'лимиты не заданы';
}

const JOBS = [
  { key: 'subscription/auto-renew', title: 'продление подписок', schedule: 'каждый день 03:00' },
  { key: 'subscription/expire', title: 'завершение истёкших', schedule: 'каждый день 03:10' },
  { key: 'subscription/activate-scheduled', title: 'включение отложенных', schedule: 'каждый день 03:20' },
  { key: 'subscription/cancel-stale-unpaid', title: 'отмена зависших неоплаченных', schedule: 'каждый день 03:30' },
  { key: 'subscription/process-trial-expirations', title: 'обработка окончаний триала', schedule: 'каждый день 04:00' },
  { key: 'subscription/trial-expiration-warnings-3-days', title: 'письма «триал кончается через 3 дня»', schedule: 'каждый день 09:00' },
  { key: 'subscription/trial-expiration-warnings-1-day', title: 'письма «триал кончается завтра»', schedule: 'каждый день 09:05' },
  { key: 'retention/tick', title: 'удаление данных по сроку', schedule: 'каждый день 05:00' },
  { key: 'notification/batch-flush', title: 'отправка накопленных уведомлений', schedule: 'каждые 15 минут' },
];

export default function Ops({ nonce }) {
  const plans = useApi(() => loadPlans(api), [nonce]);
  const { canEdit } = useAuth();

  return (
    <>
      <div className="grid cards-2">
        <Card className="pad">
          <CardHead title="тарифы" caption="все рабочие тарифы, включая те, которых нет на сайте" />
          {plans.loading ? <Loading what="тарифы" />
            : plans.error ? <ErrorState error={plans.error} onRetry={plans.reload} />
            : (
              <div className="list">
                {plans.data.map((p) => {
                  const price = monthlyPrice(p);
                  return (
                    <div className="row" key={p.id}>
                      <span className="rn">
                        <PlanBadge plan={p.name} published={p.onSite !== false} note={p.description} />
                        <div className="t-sub">{limitsLine(p)}</div>
                      </span>
                      <span className="rv">{price === null ? '—' : price ? rub(price) + '/мес' : 'бесплатно'}</span>
                    </div>
                  );
                })}
              </div>
            )}
          <div className="note" style={{ marginTop: 12 }}>
            Тариф работает, даже если он не опубликован: подписку на него выдают по прямой ссылке,
            а на сайте его не видно. Список здесь всегда полный.
          </div>
        </Card>

        <Card className="pad">
          <CardHead title="доступ в админку" caption="кого пускать — решает глобальная роль" />
          <div className="note">
            Отдельного списка допущенных у админки нет: внутрь пускает роль администратора
            у учётной записи 7cut. Выдаётся она не здесь — это сделано намеренно, чтобы
            раздача глобальных прав не превращалась в кнопку.
          </div>
        </Card>
      </div>

      {/* Убрано под спойлер: ручной запуск делает то же, что ночная задача —
          списывает деньги, рассылает письма, удаляет данные. */}
      <details className="card pad fold" style={{ marginTop: 12 }}>
        <summary>
          <span className="fold-t">фоновые задачи</span>
          <span className="fold-c">сюда заходить обычно не нужно — сервис делает это сам</span>
        </summary>
        <div className="fold-b">
          <p className="capt">
            Эти задачи идут по расписанию без участия человека: списывают деньги, закрывают триалы,
            шлют письма, удаляют данные по сроку. Запускать вручную нужно в двух случаях: задача
            не отработала ночью, или вы руками поправили данные и хотите применить результат сразу.
          </p>
          <div className="note warn" style={{ marginBottom: 14 }}>
            Запуск вручную делает всё то же, что ночью: списывает деньги с карт, рассылает письма,
            удаляет данные. Отменить нельзя.
          </div>
          <div className="list">
            {JOBS.map((j) => (
              <div className="row" key={j.key}>
                <span className="rn">{j.title}<div className="t-sub">{j.schedule}</div></span>
                {canEdit ? <RunButton job={j} /> : null}
              </div>
            ))}
          </div>
          <div className="note" style={{ marginTop: 14 }}>
            Чего здесь не хватает: когда задача отработала в последний раз и чем кончилась.
            Сервер историю запусков не хранит, поэтому показать нечего.
          </div>
        </div>
      </details>
    </>
  );
}

/* Запуск в два клика: случайное нажатие ничего не делает. */
function RunButton({ job }) {
  const [state, setState] = useState('idle');

  if (state === 'done') return <button className="btn small" disabled style={{ color: 'var(--ok)' }}>запущено</button>;
  if (state === 'busy') return <button className="btn small" disabled>запускаем…</button>;

  if (state === 'armed') {
    setTimeout(() => setState((s) => (s === 'armed' ? 'idle' : s)), 4000);
    return (
      <button className="btn small danger" onClick={async () => {
        setState('busy');
        try { await api.runJob(job.key); setState('done'); setTimeout(() => setState('idle'), 2500); }
        catch { setState('idle'); }
      }}>точно запустить?</button>
    );
  }

  return <button className="btn small" onClick={() => setState('armed')}>запустить</button>;
}
